<header class="workflow-editor-header">
  <div class="workflow-editor-heading">
    <h2 class="workflow-editor-title" [title]="$title()">{{ $title() }}</h2>
    <p class="workflow-editor-status" aria-live="polite">
      <span>{{ editor.status }}</span>
      @if ($revision(); as revision) { <span>{{ revision }}</span> }
      @if ($page().$dirty()) { <span class="workflow-editor-dirty">Unsaved changes</span> }
    </p>
  </div>
  <div class="workflow-editor-commands">
    @if (editor.headerActions) {
      <ng-container [ngComponentOutlet]="editor.headerActions" [ngComponentOutletInputs]="$headerInputs()" />
    }
    <wb-button iconLeft="fa-regular fa-sliders" [label]="$settingsLabel()" [size]="ButtonSize.Medium" [displayStyle]="$settingsOpen() ? ButtonDisplayStyle.Secondary : ButtonDisplayStyle.Tertiary" (onClick)="toggleSettings()" />
    <wb-button-icon iconLeft="fa-regular fa-arrow-rotate-left" [tooltip]="$undoTooltip" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Tertiary" [disabled]="!$canUndo()" (onClick)="undo()" />
    <wb-button-icon iconLeft="fa-regular fa-arrow-rotate-right" [tooltip]="$redoTooltip" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Tertiary" [disabled]="!$canRedo()" (onClick)="redo()" />
    @if ($issues().length) {
      <wb-button iconLeft="fa-regular fa-circle-exclamation" [label]="$issues().length === 1 ? '1 issue' : $issues().length + ' issues'" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Secondary" (onClick)="toggleIssues()" />
    }
    @if ($actions().length) { <wb-action-bar [commands]="$actions()" /> }
    @if (!$readonly()) {
      <wb-button [label]="$submitLabel()" [tooltip]="$submitTooltip()" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Primary" [disabled]="$submitDisabled()" [loading]="$busy()" (onClick)="submit()" />
    }
    <wb-button-icon iconLeft="fa-regular fa-xmark" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Tertiary" tooltip="Close" (onClick)="close()" />
  </div>
</header>
@if ($banner(); as banner) {
  <ng-container [ngComponentOutlet]="banner.component" [ngComponentOutletInputs]="banner.inputs?.() ?? {}" />
}
@if ($confirmDiscard()) {
  <div class="workflow-editor-confirm" role="alert">
    <span>Discard unsaved changes?</span>
    <wb-button label="Keep editing" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Secondary" (onClick)="$confirmDiscard.set(false)" />
    <wb-button label="Discard changes" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Primary" (onClick)="discard()" />
  </div>
}
@if ($errors().length) {
  <div class="workflow-editor-errors" role="alert">@for (error of $errors(); track $index) { <p>{{ error.message }}</p> }</div>
}
<div class="workflow-editor-body">
  <main class="workflow-editor-canvas">
    <div class="workflow-editor-canvas-scroll">
      <ng-container [ngComponentOutlet]="editor.canvas" [ngComponentOutletInputs]="$canvasInputs()" />
    </div>
  </main>

  @if ($settingsOpen()) {
    <aside class="workflow-settings-drawer" [attr.aria-label]="$settingsLabel() + ' settings'">
      <header class="workflow-settings-drawer-header">
        <h3>{{ $settingsLabel() }}</h3>
        <wb-button-icon iconLeft="fa-regular fa-xmark" tooltip="Close settings" [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Tertiary" (onClick)="$settingsOpen.set(false)" />
      </header>
      <div class="workflow-settings-drawer-body">
        @for (item of $settingsSections(); track item.section.path) {
          <ng-container [ngTemplateOutlet]="section" [ngTemplateOutletContext]="{ $implicit: item }" />
        }
      </div>
    </aside>
  }

  @if ($issuesOpen()) {
    <aside class="workflow-issues-popover" aria-label="Issues">
      <header class="workflow-issues-popover-header">
        <h3>{{ $issues().length === 1 ? '1 thing to fix before saving' : $issues().length + ' things to fix before saving' }}</h3>
        <wb-button-icon iconLeft="fa-regular fa-xmark" tooltip="Close issues" [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Tertiary" (onClick)="$issuesOpen.set(false)" />
      </header>
      @if ($issues().length) {
        <ul class="workflow-editor-issues" aria-label="Issues">
          @for (issue of $issues(); track $index) {
            <li>
              <button type="button" (click)="openIssue(issue)">
                <span class="workflow-editor-issue-dot" aria-hidden="true"></span>
                <span class="workflow-editor-issue-body">
                  @if (issue.context) {
                    <span class="workflow-editor-issue-context">{{ issue.context }}@if (issue.fieldLabel) { · {{ issue.fieldLabel }} }</span>
                  }
                  <span class="workflow-editor-issue-message">{{ issue.message }}</span>
                </span>
              </button>
            </li>
          }
        </ul>
        <footer class="workflow-issues-popover-footer">Click one to open where it is</footer>
      } @else {
        <p class="workflow-editor-rail-hint">No issues. This can be saved.</p>
      }
    </aside>
  }

  @if ($sheetInputs(); as sheetInputs) {
    <ng-container [ngComponentOutlet]="editor.stepSheet" [ngComponentOutletInputs]="sheetInputs" />
  }

  @if ($sidePanel(); as sidePanel) {
    <ng-container [ngComponentOutlet]="sidePanel" [ngComponentOutletInputs]="$sidePanelInputs()" />
  }
</div>
<ng-template #section let-item>
  <wb-section [path]="item.section.path" [title]="item.$title()" [description]="item.section.description" [columns]="item.section.columns" [layout]="item.section.layout"
    [collapsible]="item.section.collapsible" [collapsedByDefault]="item.section.collapsedByDefault" [readonly]="$page().section(item.section.path).$readonly() || $busy()"
    [fields]="item.fields" [fieldGroups]="item.fieldGroups" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs"
    [surfaceMode]="$surfaceMode()" [page]="$page()" />
</ng-template>
